import { File, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import {type ServiceModuleModel, ServiceModuleType} from "@/api/model";
import {SERVICE_MODULE_TYPES} from "@/pages/service-modules/grid-components/ServiceModuleCard.tsx";
import {cn} from "@/lib/utils.ts";
import {iconForName} from "@/components/common/iconByName.tsx";


export function FolderTree({ folders, parentId, depth, selected, onSelect, disabledIds }: {
    folders: ServiceModuleModel[]
    parentId: number | null
    depth: number
    selected: number | null
    onSelect: (id: number | null) => void
    disabledIds: Set<number>
}) {
    const [openFolderIds, setOpenFolderIds] = useState<Set<number>>(new Set())

    const children = folders.filter(f => (f.parentFolderServiceModuleId ?? null) === parentId)

    if (children.length === 0) return null

    function toggleFolder(id: number) {
        setOpenFolderIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    return (
        <>
            {children.map(folder => {
                const isFolder = folder.type === ServiceModuleType.Folder
                const isDisabled = !isFolder || disabledIds.has(folder.id)
                const isOpen = openFolderIds.has(folder.id)
                const hasChildren = folders.some(f => f.parentFolderServiceModuleId === folder.id)

                const typeEntry = SERVICE_MODULE_TYPES.find(e => e.value === folder.type)
                const Icon = iconForName(folder.iconName, typeEntry?.Icon ?? File)
                const color = folder.colorHex || '#64748b'

                return (
                    <div key={folder.id}>
                        <button
                            disabled={isDisabled}
                            className={cn(
                                'w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2',
                                !isDisabled && 'hover:bg-muted cursor-pointer',
                                isDisabled && 'cursor-default opacity-40',
                                selected === folder.id && 'bg-primary/10 text-primary font-medium ring-1 ring-primary/20'
                            )}
                            style={{ paddingLeft: `${(depth + 1) * 12}px` }}
                            onClick={() => {
                                if (isDisabled) return
                                toggleFolder(folder.id)
                                onSelect(folder.id)
                            }}
                        >
                            {folder.iconUrl ? (
                                <img src={folder.iconUrl} alt="" className="size-4 shrink-0 rounded object-cover" />
                            ) : (
                                <Icon className="size-4 shrink-0" style={{ color }} />
                            )}
                            {folder.name ?? '—'}
                            {isFolder && hasChildren && (
                                <ChevronRight className={cn(
                                    'size-3.5 ml-auto text-muted-foreground transition-transform',
                                    isOpen && 'rotate-90'
                                )} />
                            )}
                        </button>

                        {isOpen && (
                            <FolderTree
                                folders={folders}
                                parentId={folder.id}
                                depth={depth + 1}
                                selected={selected}
                                onSelect={onSelect}
                                disabledIds={disabledIds}
                            />
                        )}
                    </div>
                )
            })}
        </>
    )
}
